<template>
  <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <!-- Header with back button -->
    <div class="flex items-center mb-8">
      <button @click="goBack" class="mr-4 p-2 rounded-lg hover:bg-gray-100">
        <img alt="Back" class="w-5 h-5" />
      </button>
      <h2 class="text-2xl font-bold text-gray-800">{{ reportTitle }} Report</h2>
    </div>

    <!-- Filter form -->
    <form
      @submit.prevent="generateReport"
      class="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <!-- Date Range (Common to all reports) -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2"
          >Date Range</label
        >
        <div class="flex items-center space-x-4">
          <input
            type="date"
            v-model="filters.startDate"
            required
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
          <span class="text-gray-500">to</span>
          <input
            type="date"
            v-model="filters.endDate"
            required
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
        </div>
      </div>

      <!-- Dynamic filters based on report type -->
      <template v-if="reportType === 'hours'">
        <!-- Hours Report Specific Filters -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2"
            >Source Type</label
          >
          <select
            v-model="filters.workedHoursType"
            @change="fetchUsers"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="Employees">Employees</option>
            <option value="Contractors">Contractors</option>
            <option value="Projects">Projects</option>
          </select>
        </div>

        <div class="mb-6" v-if="typeOptions.length > 0">
          <label class="block text-sm font-medium text-gray-700 mb-2">{{
            filters.workedHoursType
          }}</label>
          <select
            v-model="filters.selectedUser"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <div
              v-if="
                filters.workedHoursType === 'Employees' ||
                filters.workedHoursType === 'Contractors'
              "
            >
              <option
                v-for="user in typeOptions"
                :key="user.id_user"
                :value="user.id_user"
              >
                {{ `${user.name} ${user.last_name}` }}
              </option>
            </div>
            <div v-else>
              <option
                v-for="project in typeOptions"
                :key="project.id_project"
                :value="project.id_project"
              >
                {{ `${project.name}` }}
              </option>
            </div>
          </select>
        </div>

        <div class="mb-6 flex items-center">
          <input
            type="checkbox"
            v-model="filters.includeBreakdown"
            id="includeBreakdown"
            class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label for="includeBreakdown" class="ml-2 block text-sm text-gray-700"
            >Include Detailed Breakdown</label
          >
        </div>
      </template>

      <template v-else-if="reportType === 'timesheets'">
        <!-- Timesheets Report Specific Filters -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2"
            >Status</label
          >
          <select
            v-model="filters.status"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="all">All</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </template>

      <template v-else-if="reportType === 'clients'">
        <!-- Clients Report Specific Filters -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2"
            >Client</label
          >
          <select
            v-model="filters.clientId"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option
              v-for="client in typeOptions"
              :key="client.id"
              :value="client.id"
            >
              {{ client.name }} {{ client.last_name }}
            </option>
          </select>
        </div>

      
      </template>

      <!-- Action buttons -->
      <div class="flex justify-between mt-8">
        <button
          type="button"
          @click="resetFilters"
          class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Reset Filters
        </button>

        <button
          type="submit"
          class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Generate Report
        </button>
      </div>
    </form>
  </div>
</template>
<script>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

export default {
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const reportType = ref(route.params.type);
    const typeOptions = ref([]);

  

    // Default filters structure
    const filters = ref({
      startDate: "",
      endDate: "",
      userTimezone: userTimezone,
    });
    console.log("🚀 ~ filters:", filters.value)

    // Format date as YYYY-MM-DD for input fields
    const formatDate = (date) => {
      return date.toISOString().split("T")[0];
    };

    // Initialize filters based on report type
    const initializeFilters = async() => {
      // Reset filters primero
      filters.value = {
        startDate: "",
        endDate: "",
      };

      // Configuración específica por tipo de reporte
      switch (reportType.value) {
        case "hours":
          filters.value = {
            ...filters.value,
            workedHoursType: "",
            selectedUser: "",
            includeBreakdown: false,
          };
          break;
        case "timesheets":
       
          break;
        case "clients":
         filters.value = {
            ...filters.value,
            client: ""
          };
          break;
        case "expenses":
          // Configuración para expenses
          break;
        case "bills":
          // Configuración para bills
          break;
        case "projects":
          // Configuración para projects
          break;
      }

      // Set default dates (current month)
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      filters.value.startDate = formatDate(firstDay);
      filters.value.endDate = formatDate(lastDay);

      // Si es reporte de horas, cargar usuarios iniciales
      if (reportType.value === "hours") {
        fetchUsers();
      } else if (reportType.value === "clients") {
        await store.dispatch("fetchAllClients")
        typeOptions.value = store.getters.detailsData || [];
      }
    };

    // Dynamic report title based on type
    const reportTitle = computed(() => {
      const titles = {
        hours: "Worked Hours",
        timesheets: "Timesheets",
        clients: "Clients",
        expenses: "Expenses",
        bills: "Bills",
        projects: "Projects",
      };
      return titles[reportType.value] || "Report";
    });

    // Navigation back to reports main
    const goBack = () => {
      router.push({ name: "reports" });
    };

    // Generate report handler
    const generateReport = () => {
      console.log("Generating report with filters:", filters.value);
      console.log("Report type:", reportType.value);

      // Aquí puedes:
      // 1. Validar filtros
      // 2. Llamar API para generar reporte
      // 3. Navegar a la vista de resultados

      // Ejemplo de navegación a resultados:
      router.push({
        name: "reports-results",
        params: { type: reportType.value },
        query: { filters: JSON.stringify(filters.value) },
      });
    };

    // Reset filters to defaults
    const resetFilters = () => {
      initializeFilters();
    };

    const fetchUsers = async () => {
      try {
        if (filters.value.workedHoursType === "Employees") {
          const result = await store.dispatch("fetchEmployees");
          typeOptions.value = result.data || [];
        } else if (filters.value.workedHoursType === "Contractors") {
          const result = await store.dispatch("fetchContractors");
          typeOptions.value = result.data || [];
        } else {
          const result = await store.dispatch("fetchActiveProjects");
          console.log("🚀 ~ fetchUsers ~ result:", result);
          typeOptions.value = result.data || [];
        }

        // Reset selected user cuando cambia el tipo
        filters.value.selectedUser = "";
      } catch (error) {
        console.error("Error fetching users:", error);
        typeOptions.value = [];
      }
    };

    // Initialize component
    onMounted(() => {
      initializeFilters();
    });

    return {
      reportType,
      filters,
      reportTitle,
      goBack,
      generateReport,
      resetFilters,
      fetchUsers,
      typeOptions,
    };
  },
};
</script>